package uk.co.nickthecoder.kyd.shapes

import uk.co.nickthecoder.glok.application.Platform

/**
 * Data used by a Shader Program comprising a stream of floats.
 * In the simplest case, these float come in groups of 6 forming the 3 vertices of a triangle.
 *
 * However, if [isTriangleStrip] == `true`, then the data is compressed. The first 6 floats are a "normal" triangle,
 * but from then on, each new triangle only uses 2 floats, with two of the triangle's vertices being defined by
 * the previous 4 floats.
 *
 * Meshes may also include so called `uv` data. In this case each vertex has additional data.
 * Therefore, each vertex can have more than 2 floats; 4 if we have x, y, u and v; 3 if we have x, y and u.
 *
 * The latter is important, as this is the form used for stroke meshes. `u` in this case is either `0f` for vertices
 * on the inside edge of the stroke and `1f` for vertices on the outside of the stroke.
 */
class Mesh(

    val meshBuffer: MeshBuffer,
    val isTriangleStrip: Boolean = false,
    val floatsPerVertex: Int = 2

) {

    internal fun destroy() {
        meshBuffer.delete()
    }

}

/**
 * A wrapper around OpenGL ARRAY_BUFFER for holding vertex data.
 */
expect class MeshBuffer {

    constructor(data: Collection<Float>)
    constructor(data: FloatArray)

    val floatCount : Int

    fun bind()
    fun delete()

    companion object {
        fun fromTriangles(triangles: Collection<Triangle>): MeshBuffer
    }
}

internal class TriangleFloatIterator(triangles: Iterable<Triangle>) : Iterator<Float> {

    private val triangleIterator = triangles.iterator()
    private var triangle: Triangle? = if (triangleIterator.hasNext()) triangleIterator.next() else null
    private var point = triangle?.a

    /**
     * 0 = Triangle.x, 1 = Triangle.b 2 = Triangle.c
     */
    private var pointNumber = 0
    private var sentX = false

    override fun hasNext() = point != null

    override fun next(): Float {
        val currentTriangle = triangle ?: throw NoSuchElementException()
        val currentPoint = point ?: throw NoSuchElementException()

        if (sentX) {
            sentX = false
            when (pointNumber) {

                0 -> {
                    pointNumber ++
                    point = currentTriangle.b
                }

                1 -> {
                    pointNumber ++
                    point = currentTriangle.c
                }

                else -> {
                    pointNumber = 0
                    if (triangleIterator.hasNext()) {
                        triangle = triangleIterator.next().also {
                            point = it.a
                        }
                    } else {
                        point = null
                        triangle = null
                    }
                }
            }
            return currentPoint.y
        } else {
            sentX = true
            return currentPoint.x
        }
    }

}
